import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePersonelList } from '@/features/hr/personel/hooks/usePersonelStore'
import { cn } from '@/lib/utils'

export function PersonelPicker({
  selectedIds,
  onChange,
}: {
  selectedIds: string[]
  onChange: (ids: string[]) => void
}) {
  const { t } = useTranslation()
  const [q, setQ] = useState('')
  const list = usePersonelList()

  const filtered = useMemo(() => {
    const n = q.trim().toLocaleLowerCase('tr')
    return list.filter(
      (p) =>
        !n ||
        p.ad.toLocaleLowerCase('tr').includes(n) ||
        p.departman.toLocaleLowerCase('tr').includes(n) ||
        p.sicil.toLocaleLowerCase('tr').includes(n),
    )
  }, [list, q])

  const selected = list.filter((p) => selectedIds.includes(p.id))
  const shown = selected.slice(0, 3)
  const extra = Math.max(0, selected.length - shown.length)

  function toggle(id: string) {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id])
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t('hr.belgeler.publish.affected')}</span>
        <div className="flex -space-x-2">
          {shown.map((p) => (
            <Avatar key={p.id} name={p.ad} className="size-8 border-2 border-card text-[10px]" />
          ))}
          {extra > 0 ? (
            <span className="flex size-8 items-center justify-center rounded-full border-2 border-card bg-muted text-[10px] font-semibold">
              +{extra}
            </span>
          ) : null}
        </div>
        <span className="text-xs font-semibold">{t('hr.belgeler.publish.employeeCount', { n: selected.length })}</span>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="h-9 pl-8"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('hr.belgeler.publish.searchPersonel')}
        />
      </div>

      <ul className="max-h-48 space-y-1 overflow-y-auto scrollbar-none rounded-xl border border-border/70 p-1">
        {filtered.map((p) => {
          const on = selectedIds.includes(p.id)
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => toggle(p.id)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm',
                  on ? 'bg-primary/10' : 'hover:bg-muted/60',
                )}
              >
                <input type="checkbox" readOnly checked={on} className="accent-primary" />
                <Avatar name={p.ad} className="size-7 text-[10px]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{p.ad}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {p.departman} · {p.pozisyon}
                  </div>
                </div>
              </button>
            </li>
          )
        })}
      </ul>

      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => onChange(list.map((p) => p.id))}>
          {t('hr.belgeler.publish.selectAll')}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange([])}>
          {t('hr.belgeler.publish.clear')}
        </Button>
      </div>
    </div>
  )
}
